import { useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
} from '@mui/material';

import {
  isAuthenticatedAtom,
  locationsAtom,
  navbarHeightAtom,
  selectedStateAtom,
} from '../utils/AtomConfig';
import Constants from '../utils/Constants';
import { SharePointService } from '../utils/SharePointService';
import { AccessRestricted } from './AccessRestricted';
import { GenericError } from './GenericError';

export function Home() {
  const setLocations = useSetAtom(locationsAtom);
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const navbarHeight = useAtomValue(navbarHeightAtom);
  const setSelectedState = useSetAtom(selectedStateAtom);
  const history = useHistory();

  const [hasNoAccess, setHasNoAccess] = useState(false);
  const [hasGenericError, setHasGenericError] = useState(false);

  const sharepointService = SharePointService.Instance;
  const locationsQuery = useQuery(
    Constants.QUERY_KEYS.LOCATIONS,
    () => sharepointService.getLocations(),
    {
      ...Constants.BASE_DISABLE_REFETCH_SETTINGS,
      enabled: isAuthenticated,
      onError: (error: any) => {
        if (error.response && error.response.status === 403) {
          setHasNoAccess(true);
        } else {
          setHasGenericError(true);
        }
      },
    }
  );

  useEffect(() => {
    if (locationsQuery.data) {
      setLocations(locationsQuery.data);
    }
  }, [locationsQuery.data, setLocations]);

  const isLoading = locationsQuery.isLoading || locationsQuery.isIdle;

  if (hasNoAccess) {
    return <AccessRestricted />;
  }

  if (hasGenericError) {
    return <GenericError />;
  }

  return (
    <Box
      sx={{
        backgroundColor: '#ECECF7',
        paddingLeft: {
          xs: 2,
          sm: 4,
        },
        paddingRight: {
          xs: 2,
          sm: 4,
        },
        paddingBottom: {
          xs: 2,
          sm: 4,
        },
        paddingTop: `${navbarHeight}px`,
        minHeight: `100vh`,
      }}
    >
      <Typography
        variant='h1'
        gutterBottom
        sx={{
          fontWeight: 'bold',
          color: '#1A237E',
          fontSize: '3rem',
          marginTop: 4,
          textAlign: 'center',
        }}
      >
        Whitehaven Information Centre
      </Typography>

      <Typography
        variant='subtitle1'
        color='textSecondary'
        sx={{ marginBottom: 4, marginTop: 4, textAlign: 'center' }}
      >
        Please select a state to access the corresponding document repositories
      </Typography>

      <Container maxWidth='md'>
        <Grid container spacing={4}>
          {Constants.STATES.map((state) => (
            <Grid item key={state.id} xs={12} sm={6}>
              <Card
                sx={{
                  borderRadius: 4,
                  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <CardContent
                  sx={{ textAlign: 'center', padding: 4, paddingBottom: 3 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginBottom: 2,
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: '#e3f2fd',
                      margin: '0 auto 16px',
                    }}
                  >
                    <LocationOnIcon sx={{ fontSize: 40, color: '#003591' }} />
                  </Box>
                  <Typography
                    variant='h4'
                    component='div'
                    sx={{ fontWeight: 'bold', marginBottom: 1 }}
                  >
                    {state.title}
                  </Typography>
                  <Typography variant='body1' color='textSecondary'>
                    Access {state.id} documents repositories
                  </Typography>
                </CardContent>
                <Box sx={{ padding: '0 24px 24px' }}>
                  <Button
                    fullWidth
                    variant='contained'
                    color='primary'
                    onClick={() => {
                      setSelectedState(state.id);
                      history.push('/state');
                    }}
                    disabled={isLoading}
                    sx={{
                      padding: '10px 0',
                      fontWeight: 'bold',
                    }}
                  >
                    Select {state.id}
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
